import { useEffect, useMemo, useState } from "react";
import { fetchMemberNoRule, putMemberNoRule, type MemberNoRuleDto } from "../../../../api/memberNoRule";
import { fetchMemberLevelsForTenant } from "../../../../api/memberLevels";
import { getUserFacingMessage, reportError, showUserNotice } from "../../../../api/errors";
import type { MemberLevel } from "../../utils/memberLevelStore";
import { useAdminData } from "../../AdminDataContext";
import { composeMemberNoFromRule, padMemberNoSeq } from "../../utils/memberNoCompose";
import ReservedMemberNoPanel from "./ReservedMemberNoPanel";

type Row = { id: string; levelName: string; prefix: string };

function sortLevels(levels: MemberLevel[]) {
  return [...levels].sort((a, b) => a.sortOrder - b.sortOrder);
}

/** 当前行可选等级：字典中尚未被其它行占用的项 + 本行已选（含历史遗留名） */
function levelSelectOptions(rows: Row[], row: Row, dictLevels: MemberLevel[]): { value: string; label: string }[] {
  const cur = row.levelName.trim();
  const usedByOthers = new Set(
    rows.filter((x) => x.id !== row.id).map((x) => x.levelName.trim()).filter(Boolean),
  );
  const out: { value: string; label: string }[] = [];
  for (const lv of sortLevels(dictLevels)) {
    const n = lv.name.trim();
    if (!n) continue;
    if (!usedByOthers.has(n) || n === cur) out.push({ value: n, label: n });
  }
  if (cur && !out.some((o) => o.value === cur)) {
    out.unshift({ value: cur, label: `${cur}（未在等级字典，请改选）` });
  }
  return out;
}

export default function MemberNoRuleConfig() {
  const { tenantLegacyId } = useAdminData();
  const [activeTab, setActiveTab] = useState<"rule" | "reserved">("rule");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [tenantPrefix, setTenantPrefix] = useState("");
  const [separator, setSeparator] = useState("");
  const [digits, setDigits] = useState(4);
  const [seqNext, setSeqNext] = useState(1);
  const [rows, setRows] = useState<Row[]>([]);
  /** 会员等级字典（与「系统设置 → 会员等级」同源） */
  const [dictLevels, setDictLevels] = useState<MemberLevel[]>([]);

  const mapping = useMemo(() => {
    const out: Record<string, string> = {};
    for (const r of rows) {
      const k = (r.levelName || "").trim();
      if (!k) continue;
      const v = (r.prefix || "")
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9-]/g, "")
        .slice(0, 12);
      out[k] = v;
    }
    return out;
  }, [rows]);

  const sample = useMemo(() => {
    const levelName =
      rows.find((r) => r.levelName.trim())?.levelName.trim() ||
      sortLevels(dictLevels)[0]?.name?.trim() ||
      "普通会员";
    return composeMemberNoFromRule(
      {
        tenantPrefix: (tenantPrefix || "").trim(),
        separator: (separator || "").trim(),
        digits,
        levelPrefixes: mapping,
      },
      levelName,
      padMemberNoSeq(seqNext, digits),
    );
  }, [dictLevels, digits, mapping, rows, separator, seqNext, tenantPrefix]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    void Promise.all([
      fetchMemberNoRule(tenantLegacyId),
      fetchMemberLevelsForTenant(tenantLegacyId).catch(() => [] as MemberLevel[]),
    ])
      .then(([d, lvList]) => {
        if (cancelled) return;
        const lv = Array.isArray(lvList) ? lvList : [];
        setDictLevels(lv);

        const dto: MemberNoRuleDto = d;
        setTenantPrefix(String(dto.tenantPrefix ?? "").trim());
        setSeparator(String(dto.separator ?? "").trim());
        setDigits(Number(dto.digits) || 4);
        setSeqNext(Number(dto.seqNext) || 1);
        const map = dto.levelPrefixes ?? {};
        const levelNames = sortLevels(lv)
          .map((x) => x.name.trim())
          .filter(Boolean);
        const list: Row[] = levelNames.length
          ? levelNames.map((k) => ({
              id: `r-${k}`,
              levelName: k,
              prefix: String(map[k] ?? ""),
            }))
          : Object.keys(map).map((k) => ({
              id: `r-${k}`,
              levelName: k,
              prefix: String(map[k] ?? ""),
            }));
        if (list.length) {
          setRows(list);
          return;
        }
        const first = sortLevels(lv)[0];
        const nm = first?.name?.trim() ?? "";
        if (nm) {
          setRows([{ id: "r-0", levelName: nm, prefix: "" }]);
        } else {
          setRows([{ id: "r-0", levelName: "", prefix: "" }]);
        }
      })
      .catch((e) => {
        if (!cancelled) setLoadError(getUserFacingMessage(e, "加载失败"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantLegacyId]);

  const normalizePrefix = (raw: string, maxLen: number) =>
    raw
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9-]/g, "")
      .slice(0, maxLen);

  const handleSave = () => {
    void (async () => {
      const tp = normalizePrefix(tenantPrefix || "", 24);
      const sep = (separator || "").trim().slice(0, 4);
      const d = Math.max(2, Math.min(10, Number(digits) || 4));
      const sn = Math.max(1, Math.floor(Number(seqNext) || 1));

      setSaving(true);
      try {
        await putMemberNoRule(tenantLegacyId, {
          tenantPrefix: tp,
          separator: sep,
          digits: d,
          seqNext: sn,
          levelPrefixes: mapping,
        });
        setTenantPrefix(tp);
        setSeparator(sep);
        setDigits(d);
        setSeqNext(sn);
        showUserNotice("已保存会员编号规则。");
      } catch (e) {
        reportError(e, "保存失败");
      } finally {
        setSaving(false);
      }
    })();
  };

  const addRow = () => {
    setRows((prev) => [...prev, { id: `r-${Date.now()}`, levelName: "", prefix: "" }]);
  };

  const removeRow = (id: string) => {
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.id !== id)));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-bold text-gray-900">会员编号规则</h3>
          <p className="text-xs text-gray-400 mt-0.5">用于会员审核时自动生成会员编号（租户前缀 + 等级前缀 + 固定位数序号）</p>
          {loadError && <p className="text-xs text-amber-600 mt-1">加载失败：{loadError}</p>}
        </div>
        {activeTab === "rule" ? (
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="flex items-center gap-2 px-5 py-2 text-sm rounded-xl cursor-pointer transition-all whitespace-nowrap disabled:opacity-50 bg-red-600 text-white hover:bg-red-700"
          >
            <i className={saving ? "ri-loader-2-line animate-spin" : "ri-save-line"}></i>
            {saving ? "保存中…" : "保存规则"}
          </button>
        ) : null}
      </div>

      <div className="mb-6 inline-flex rounded-xl bg-gray-100 p-1">
        {(
          [
            { key: "rule" as const, label: "编号规则", icon: "ri-hashtag" },
            { key: "reserved" as const, label: "预留编号", icon: "ri-vip-crown-line" },
          ] as const
        ).map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setActiveTab(t.key)}
            className={`flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium transition-all ${
              activeTab === t.key ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <i className={t.icon} />
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === "rule" ? (
      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-1 space-y-4">
          <div className="bg-white rounded-2xl p-5">
            <p className="text-xs font-semibold text-gray-600 mb-4 flex items-center gap-2">
              <i className="ri-hashtag text-red-500"></i> 规则参数
            </p>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">租户前缀</label>
                <input
                  value={tenantPrefix}
                  onChange={(e) => setTenantPrefix(e.target.value)}
                  placeholder="默认取租户 Code（如：JA）"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 transition-colors font-mono"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">分隔符</label>
                <input
                  value={separator}
                  onChange={(e) => setSeparator(e.target.value)}
                  placeholder="可留空（直接拼接）"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 transition-colors font-mono"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">序号位数</label>
                  <input
                    type="number"
                    value={digits}
                    onChange={(e) => setDigits(Number(e.target.value))}
                    min={2}
                    max={10}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">起始序号</label>
                  <input
                    type="number"
                    value={seqNext}
                    onChange={(e) => setSeqNext(Number(e.target.value))}
                    min={1}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 transition-colors"
                  />
                </div>
              </div>

              <div className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-2.5">
                <p className="text-xs text-gray-500">示例预览</p>
                <p className="mt-1 text-sm font-mono font-bold text-gray-800">{sample}</p>
                <p className="mt-1 text-[11px] text-gray-400">预览仅用于展示，实际编号以服务端生成为准。</p>
              </div>
            </div>
          </div>
        </div>

        <div className="col-span-2 space-y-4">
          <div className="bg-white rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs font-semibold text-gray-600 flex items-center gap-2">
                <i className="ri-vip-crown-2-line text-red-500"></i> 等级前缀映射
              </p>
              <button
                type="button"
                onClick={addRow}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-xl hover:bg-gray-200 cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-add-line"></i> 新增映射
              </button>
            </div>

            {loading ? (
              <div className="text-sm text-gray-500">加载中…</div>
            ) : (
              <div className="space-y-3">
                {!dictLevels.length ? (
                  <p className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
                    未加载到会员等级数据。请先在「系统设置 → 会员等级」中维护等级字典，再回到本页配置映射。
                  </p>
                ) : null}
                {rows.map((r) => {
                  const opts = levelSelectOptions(rows, r, dictLevels);
                  const cur = r.levelName.trim();
                  return (
                  <div key={r.id} className="flex items-center gap-3">
                    <div className="flex-1">
                      <label className="block text-[11px] text-gray-400 mb-1">等级（字典）</label>
                      <select
                        value={cur}
                        onChange={(e) => {
                          const next = e.target.value;
                          setRows((prev) =>
                            prev.map((x) =>
                              x.id === r.id
                                ? {
                                    ...x,
                                    levelName: next,
                                  }
                                : x,
                            ),
                          );
                        }}
                        className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 transition-colors bg-white"
                      >
                        <option value="">请选择会员等级</option>
                        {opts.map((o) => (
                          <option key={`${r.id}-${o.value}`} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="w-[180px]">
                      <label className="block text-[11px] text-gray-400 mb-1">前缀</label>
                      <input
                        value={r.prefix}
                        onChange={(e) =>
                          setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, prefix: e.target.value } : x)))
                        }
                        placeholder="留空则仅序号"
                        className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 transition-colors font-mono"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeRow(r.id)}
                      className="mt-6 px-3 py-2.5 rounded-xl bg-gray-50 hover:bg-gray-100 text-gray-500 cursor-pointer"
                      title={rows.length <= 1 ? "至少保留一条" : "删除"}
                      disabled={rows.length <= 1}
                    >
                      <i className="ri-delete-bin-6-line" />
                    </button>
                  </div>
                  );
                })}

                <div className="rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-xs text-amber-700 leading-relaxed">
                  <p className="font-semibold mb-1">提示</p>
                  <p>等级名称须与「系统设置 → 会员等级」字典中的名称完全一致，以便审核分配等级时匹配前缀。</p>
                  <p className="mt-1">协会前缀、等级前缀均可留空；留空时该段不参与编号，全部留空则仅使用固定位数序号。</p>
                  <p className="mt-1">序号为全租户统一递增（允许跳号，不会重复）。</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      ) : (
        <ReservedMemberNoPanel tenantLegacyId={tenantLegacyId} digits={digits} variant="page" />
      )}
    </div>
  );
}

